import fieldsByScreen from './validationFields.json';
import { PortalTelemetryScreen } from './portalTelemetryScreens';
import type { FieldPath, FieldValues, UseFormGetFieldState } from 'react-hook-form';

export interface ValidationIssue { field: string; code: string }
const codes = ['required', 'optionality', 'nullable', 'typeError', 'min', 'max', 'phone',
  'email', 'url', 'matches', 'oneOf', 'notOneOf', 'integer', 'positive', 'negative',
  'length', 'len', 'not-in-future', 'valid-amount', 'allowed-prefix', 'is-correct-file',
  'is-big-file', 'invalid'];

function fieldsFor(screen?: string): readonly string[] {
  return screen && Object.prototype.hasOwnProperty.call(fieldsByScreen, screen)
    ? fieldsByScreen[screen as keyof typeof fieldsByScreen] : [];
}

function safeCode(field: string, code: unknown): string {
  // Some conditional Yup tests use the static field name as their rule name.
  return typeof code === 'string' && (codes.includes(code) || code === field) ? code : 'invalid';
}

/** Read only registered fields, never values, messages, refs or arbitrary error paths. */
export function validationIssues<TValues extends FieldValues>(screen: PortalTelemetryScreen | undefined,
  getFieldState: UseFormGetFieldState<TValues>): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const field of fieldsFor(screen)) {
    const visit = (error: unknown, depth: number) => {
      if (!error || typeof error !== 'object' || depth > 8) return;
      const item = error as Record<string, unknown>;
      if ('type' in item || 'message' in item) {
        issues.push({ field, code: safeCode(field, item.type) });
        return;
      }
      // Array item errors are folded into their static parent field identifier.
      Object.keys(item).filter(key => !['ref', 'message', 'types'].includes(key))
        .slice(0, 128).forEach(key => visit(item[key], depth + 1));
    };
    // The catalog is checked against each screen's schemas by validationCoverage.test.js.
    visit(getFieldState(field as FieldPath<TValues>).error, 0);
  }
  return sanitizeValidationIssues(screen, issues);
}

/** Repeat the allowlist check at the SDK boundary, even for manually supplied issues. */
export function sanitizeValidationIssues(screen: string | undefined, issues: unknown): ValidationIssue[] {
  if (!Array.isArray(issues)) return [];
  return fieldsFor(screen).flatMap(field => {
    const issue = issues.find(item => item && item.field === field);
    return issue ? [{ field, code: safeCode(field, issue.code) }] : [];
  });
}
